import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {API_PATH} from '../../shared/constants';
import {UnderwriterMain} from '../../shared/models/underwriter.model';
import {UnderwriterStore} from '../state/underwriter.store';
import {UserStore} from '../../authentication/state/user.store';
import {Observable} from 'rxjs';
import {delay, finalize} from 'rxjs/operators';
import {GlobalQuestionService} from '../../shared/services/global-question.service';

declare function notify(): any;

const headers = new HttpHeaders()
  .set('content-type', 'application/json')
  .set('Access-Control-Allow-Origin', '*')
  .set('Accept', 'application/json');

@Injectable({
  providedIn: 'root'
})
export class UnderwriterService {

  public wordingListMoreInfoModal: any[];
  public wordingListPill: any[];
  public wordingHistoryOriginal = [];
  public wordingHistoryChanges = [];
  public wordingHistoryCurrent = [];
  public loadingInProgress = false;
  public loadingInProgressWord = false;
  public policySessionId;
  // the landing's editPolicy result, so returning after an override can refresh without re-running editPolicy
  public landingPolicy: any = null;
  // just over the API's 120s command timeout, so a stalled request never leaves the spinner up
  public readonly overrideTimeoutMs = 125000;
  public evi = false;
  userId = 0;
  activeEntity;
  public notesModalData = {
    notesHeader: null,
    notesParseData: null
  };
  notify: any;

  constructor(
    private httpClient: HttpClient,
    private underwriterStore: UnderwriterStore,
    private userStore: UserStore,
    private questionService: GlobalQuestionService
  ) {
    this.notify = notify();
    this.userId = this.userStore.getValue()?.person_id;
  }


  // ​/reject-policy​/{session_id}
  rejectPolicy(): Observable<any> {
    return this.httpClient.get(`${API_PATH.UW_REJECT_POLICY}/${this.policySessionId}`, {headers, responseType: 'json'});
  }

  getBrokerUWLanding(): void {
    this.loadingInProgress = true;
    this.underwriterStore.reset();

    const parameters = new Map<string, string>();
    parameters.set('session_id', this.policySessionId);
    parameters.set('evi', this.evi ? '1' : '0');

    this.questionService.runCommandNew('getUwPolicyLandingData', parameters, true)
      .pipe(finalize(() => this.loadingInProgress = false))
      .subscribe((brokerUW: any) => {
        const brokerMain: UnderwriterMain[] = [];
        brokerUW.forEach((element: any, ind: number) => {
          brokerMain.push({index: ind + 1, schedule: element});
        });
        this.underwriterStore.set(brokerMain);
      });
  }

  saveCondition(): Observable<any> {
    return this.httpClient.get(`${API_PATH.UW_SAVE_CONDITION}/${this.policySessionId}`, {headers, responseType: 'json'});
  }

  // /update-premium/{session_id}/{class_id}/{item_id}/{adj_method}/{adj_val}/{user_id}
  updatePremium(params: UwHttpParams): void {
    this.httpClient.get(`${API_PATH.UW_UPDATE_PREMIUM}/${this.policySessionId}/${params?.class_id ?? 0}/${params?.item_id ?? 0}/${params?.adj_method}/${params?.adj_val}/${this.userId}`, {headers, responseType: 'json'})
      .subscribe(() => {
          this.getBrokerUWLanding();
          this.notifyToast('Successfully updated premium.', 'info');
        },
        err => {
          this.notifyToast('Failed to update premium.', 'error');
        });
  }

  getWordingList(sessionId): void {
    this.httpClient.get(`${API_PATH.UW_GET_WORDING_LIST}/${this.policySessionId}`, {headers, responseType: 'json'})
      .subscribe(wordingList => {
        this.wordingListMoreInfoModal = wordingList[0];
        this.wordingListPill = wordingList[1];
      });
  }

  addWording(wording, sessionId, policyNumber): void {
    this.loadingInProgressWord = true;
    this.httpClient.get(`${API_PATH.UW_ADD_WORDING}/${this.policySessionId}/${wording?.class_id ?? 0}/${wording?.item_id ?? 0}/${wording?.wording_id}/0/0/0`, {headers, responseType: 'json'})
      .pipe(delay(2000), finalize(() => this.loadingInProgressWord = false))
      .subscribe(addWording => {
          this.getBrokerUWLanding();
          this.getWordingHistory(this.policySessionId);
          this.notifyToast('Wording successfully added.', 'info');
        },
        err => {
          this.notifyToast('Failed to add wording.', 'error');
        });
  }

  removeWording(wording, sessionId, policyNumber): void {
    this.loadingInProgressWord = true;
    this.httpClient.get(`${API_PATH.UW_REMOVE_WORDING}/${this.policySessionId}/${wording?.class_id ?? 0}/${wording?.item_id ?? 0}/${wording?.wording_id}/0/0/0`, {headers, responseType: 'json'})
      .pipe(delay(2000), finalize(() => this.loadingInProgressWord = false))
      .subscribe(removeWording => {
          this.getBrokerUWLanding();
          this.getWordingHistory(this.policySessionId);
          this.notifyToast('Wording successfully removed.', 'info');
        },
        err => {
          this.notifyToast('Failed to remove wording.', 'error');
        });
  }

  getWordingHistory(sessionId = 0): void {
    this.httpClient.get(`${API_PATH.UW_GET_WORDING_HISTORY}/${this.policySessionId}`)
      .subscribe((history: any) => {
        if (!history?.length) {
          this.wordingHistoryOriginal = [];
          this.wordingHistoryChanges = [];
          this.wordingHistoryCurrent = [];
          return;
        }
        this.wordingHistoryOriginal = history[0];
        this.wordingHistoryChanges = history[1];
        this.wordingHistoryCurrent = history[2];
      });
  }

  // add-note/{session_id}/{class_id}/{item_id}/{vaps_id}/{question_id}/{thread_id}/{note_data}/{public}/{resolved}/{user_id}
  addNote(policyNumber, params: UwHttpParams): void {
    this.httpClient.get(`${API_PATH.UW_ADD_NOTE}/${params?.session_id}/${params?.class_id}/${params?.item_id}/${params?.vaps_id}/${params?.question_id}/${params?.thread_id}/${params?.note_data}/${params?.public}/${params?.resolved}/${this.userId}`
    ).subscribe(() => {
        this.getBrokerUWLanding();
        this.notifyToast('Note successfully added.', 'info');
      },
      err => {
        this.notifyToast('Failed to add note.', 'error');
      });
  }

  // update-note/{session_id}/{note_id}/{note_data}/{public}/{resolved}/{user_id}
  updateNote(policyNumber, params: UwHttpParams): void {
    this.httpClient.get(`${API_PATH.UW_UPDATE_NOTE}/${this.policySessionId}/${params?.uw_note_id}/${params?.note_data}/${params?.public}/${params?.resolved}/${this.userId}`
    ).subscribe(() => {
        this.getBrokerUWLanding();
        this.notifyToast('Note successfully updated.', 'info');
      },
      err => {
        this.notifyToast('Failed to update note.', 'error');
      });
  }

  // remove-note/{session_id}/{note_id}
  removeNote(policyNumber, params: UwHttpParams): void {
    params.session_id = 1614027511613;
    this.httpClient.get(`${API_PATH.UW_REMOVE_NOTE}/${params?.session_id}/${params?.uw_note_id}`
    ).subscribe(() => {
        this.getBrokerUWLanding();
        this.notifyToast('Note successfully removed.', 'info');
      },
      err => {
        this.notifyToast('Failed to remove note.', 'error');
      });
  }

  // update-answer/{session_id}/{question_id}/{class_id}/{item_id}/{answer}/{reason}/{user_id}
  updateAnswer(policyNumber, params: UwHttpParams): void {
    // params.session_id = 1614027511613;

    this.httpClient.get(`${API_PATH.UW_UPDATE_ANSWER}/${params?.session_id}/${params?.question_id}/${params?.class_id}/${params?.item_id}/${params?.answer}/${params?.reason}/${this.userId}`
    ).subscribe(() => {
        this.getBrokerUWLanding();
        this.notifyToast('Answer successfully updated.', 'info');
      },
      err => {
        this.notifyToast('Failed to update answer.', 'error');
      });
  }

  // get-answer-history​/{session_id}​/{question_id}​/{class_id}​/{item_id}
  answerHistory(params: UwHttpParams): Observable<any> {
    params.session_id = 1614027511613;

    return this.httpClient.get(`${API_PATH.UW_ANSWER_HISTORY}/${params?.session_id}/${params?.question_id}/${params?.class_id}/${params?.item_id}`);
    // .subscribe((answerHistory: any) => {
    //     if (!answerHistory?.length) {
    //       this.answerHistoryOriginal = [];
    //       this.answerHistoryCurrent = [];
    //       return;
    //     }
    //     //console.log('answer history: ', answerHistory);
    //     this.answerHistoryOriginal = answerHistory[0];
    //     this.answerHistoryCurrent = answerHistory[1];
    //     // this.getBrokerUWLanding(policyNumber, params.session_id);
    //   },
    //   err => {
    //     console.error('Failed to get answer history: ', err);
    //   });
  }

  notifyToast(message = '', style = 'info'): void {
    this.notify.init({
      message: `<strong>${message}</strong>`, // "<strong>Coming Soon! &#128522;</strong>", // required
      icon: 'info',   // default: null; options: 'info', 'complete', or any icon-name really
      size: 'lg',     // default: null; options: 'lg'
      style,  // default: null; options: 'info', 'success',
      close: true,    // default: false (close button enabled, auto-close duration removed)
      speed: 350,     // default: 350 (fade in/out speed)
      duration: 5000  // default: 5000 (duration active)
    });
  }

  parseNoteData(noteData): any {
    // //console.log('noteData: ', noteData);
    if (!noteData) {
      return [];
    }
    let returnNoteData = [];
    try {
      returnNoteData = JSON.parse(noteData);
      // //console.log('parsed JSON: ', returnNoteData);
    } catch (e) {
      console.error('Unable to parse note data from JSON: ', noteData);
    }

    return returnNoteData;
  }

  getNotesMobileData(): any[] {
    return this.parseNoteData(this.notesModalData?.notesHeader?.note_data);
  }

  formatDate(date): any {
    const newDate = new Date(date);
    const month = newDate.toLocaleString('default', {month: 'long'});
    return `${month} ${newDate.getDate()}, ${newDate.getFullYear()}`;
  }
}

export interface UwHttpParams {
  session_id?;
  class_id?;
  item_id?;
  vaps_id?;
  question_id?;
  thread_id?;
  note_data?;
  public?;
  resolved?;
  user_id?;
  uw_note_id?;
  answer?;
  reason?;
  adj_method?;
  adj_val?;
  amount?;
  excess?;
}

export enum WordCategory {
  Policy = 'Policy',
  TaxesAndFees = 'Taxes & Fees',
  Class = 'Class',
  Item = 'Item',
  Vaps = 'Vaps'
}
